import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IssueModel } from 'src/app/models/api/issue.model';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { LoggerService } from '../../infra/logger/logger.service';
import { HttpService } from '../http/http.service';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { AutocompleteSearchResultsV2Model } from 'src/app/models/api/autocompleteSearchResultsV2.model';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';

// Feedback interfaces
export interface IssueFeedbackRequest {
  feedback: 'helpful' | 'not_helpful';
  issueTitle: string;
  notes?: string;
}

export interface IssueFeedbackResponse {
  _links: {
    self: {
      href: string;
    };
  };
  issueId: string;
  zenityAccountId: string;
  feedback: string;
  issueTitle: string;
  timestamp: string;
  userEmail: string;
  notes: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class IssuesHttpService {
  public readonly defaultPageSize: number = 50;

  constructor(
    private readonly _httpService: HttpService,
    private readonly _logger: LoggerService
  ) {}

  public getIssues(
    sort?: SortColumn[],
    filters?: ISelectedFilters,
    pageSize = 50,
    pageNumber = 1
  ): Observable<JsonHalCollection<IssueModel>> {
    const url = 'assessment/issues';
    let httpParams: HttpParams = new HttpParams();

    httpParams = httpParams.append('pageSize', pageSize.toString());
    httpParams = httpParams.append('pageNumber', pageNumber.toString());

    if (sort && sort.length > 0) {
      httpParams = this._createIssuesSortQueryParams(sort, httpParams);
    }

    if (filters) {
      httpParams = this._addFilterQueryParams(httpParams, filters);
    }

    return this._httpService.get<JsonHalCollection<IssueModel>>(url, undefined, httpParams, true);
  }

  /**
   * @public Continue getting issues using a continuation token
   * @returns an Observable<JsonHalCollection<IssueModel>> with the issues returned from server
   */
  public getIssuesByContinuationToken(continuationToken: string): Observable<JsonHalCollection<IssueModel>> {
    const url = 'assessment/issues-pages';
    const httpParams = new HttpParams().append('continuationToken', continuationToken);
    return this._httpService.get<JsonHalCollection<IssueModel>>(url, undefined, httpParams, true);
  }

  /**
   * @public Start getting issues with filters and sorting (returns first page with continuation token)
   * @returns an Observable<JsonHalCollection<IssueModel>> with the first page of issues
   */
  public startGetIssues(
    filters: ISelectedFilters,
    sortData?: SortColumn,
    pageSize = this.defaultPageSize
  ): Observable<JsonHalCollection<IssueModel>> {
    const url = 'assessment/issues';
    let httpParams: HttpParams = new HttpParams();

    httpParams = httpParams.append('pageSize', pageSize.toString());

    if (sortData) {
      httpParams = this._createIssuesSortQueryParams([sortData], httpParams);
    }

    if (filters) {
      httpParams = this._addFilterQueryParams(httpParams, filters);
    }

    return this._httpService.get<JsonHalCollection<IssueModel>>(url, undefined, httpParams, true);
  }

  /**
   * @public Get a single issue by ID
   * @param integrationId The integration ID
   * @param issueId The issue ID
   * @returns an Observable<IssueModel> with the issue data
   */
  public getIssueById(integrationId: string, issueId: string): Observable<IssueModel> {
    const url = 'assessment/issues';
    let httpParams = new HttpParams();
    httpParams = httpParams.append('issue', issueId);
    httpParams = httpParams.append('page_size', '1');

    return this._httpService.get<JsonHalCollection<IssueModel>>(url, undefined, httpParams, true).pipe(
      map((response: JsonHalCollection<IssueModel>) => {
        const items = response._embedded?.items;
        if (!items || items.length === 0) {
          throw new Error(`Issue ${issueId} not found`);
        }
        return items[0];
      })
    );
  }

  public getAutocompleteOptions(
    searchTerm: string,
    filterName: string
  ): Observable<AutocompleteSearchResultsV2Model[]> {
    const url = `assessment/integrations/-/issues/autocomplete/${filterName}`;
    const httpParams = new HttpParams().append('searchTerm', searchTerm);

    return this._httpService.get<AutocompleteSearchResultsV2Model[]>(url, undefined, httpParams, true);
  }

  /**
   * @public Resolve an issue
   * @param integrationId The integration ID
   * @param issueId The issue ID
   * @returns an Observable<IssueModel> with the updated issue
   */
  public resolveIssue(integrationId: string, issueId: string): Observable<IssueModel> {
    const url = `assessment/integrations/${integrationId}/issues/${issueId}/resolve`;
    return this._httpService.put<IssueModel, Record<string, never>>(url, {}, undefined, undefined, true);
  }

  /**
   * @public Reopen an issue
   * @param integrationId The integration ID
   * @param issueId The issue ID
   * @returns an Observable<IssueModel> with the updated issue
   */
  public reopenIssue(integrationId: string, issueId: string): Observable<IssueModel> {
    const url = `assessment/integrations/${integrationId}/issues/${issueId}/reopen`;
    return this._httpService.put<IssueModel, Record<string, never>>(url, {}, undefined, undefined, true);
  }

  /**
   * Submit feedback for an issue
   * @param integrationId The integration ID
   * @param issueId The issue ID
   * @param request The feedback request containing feedback type, issue title, and optional notes
   * @returns Observable of the feedback response
   */
  public submitIssueFeedback(
    integrationId: string,
    issueId: string,
    request: IssueFeedbackRequest
  ): Observable<IssueFeedbackResponse> {
    const url = `assessment/integrations/${integrationId}/issues/${issueId}/feedback`;
    return this._httpService.post<IssueFeedbackRequest, IssueFeedbackResponse>(
      url,
      request,
      undefined,
      undefined,
      true
    );
  }

  private _createIssuesSortQueryParams(sortData: SortColumn[], httpParams: HttpParams): HttpParams {
    const sortArray: string[] = [];

    sortData.forEach((sort) => {
      const sortFieldString: string | undefined = sort.column.sortKey;
      const ascOrDescString = sort.isDesc ? 'desc' : 'asc';
      if (sortFieldString) {
        sortArray.push(`${sortFieldString}(${ascOrDescString})`);
      } else {
        this._logger.error(`Sort issues column ${sort.column.propertyKey} was not implemented`);
      }
    });

    return httpParams.append('sortBy', `[${sortArray}]`);
  }

  private _addFilterQueryParams(httpParams: HttpParams, filters: ISelectedFilters): HttpParams {
    // Search filter
    if (filters.search) {
      httpParams = httpParams.append('searchQuery', filters.search);
    }

    // Severity filter
    if (filters.severity && filters.severity.length > 0) {
      httpParams = httpParams.append('severity', filters.severity.join(','));
    }

    // Status filter
    if (filters.issueStatus && filters.issueStatus.length > 0) {
      httpParams = httpParams.append('status', filters.issueStatus.join(','));
    }

    // Origin filter
    if (filters.issueLabels && filters.issueLabels.length > 0) {
      httpParams = httpParams.append('origin', filters.issueLabels.join(','));
    }

    // Integration filter
    if (filters.integration && filters.integration.length > 0) {
      httpParams = httpParams.append('integrationId', filters.integration.join(','));
    }

    // AI Service filter
    if (filters.aiService && filters.aiService.length > 0) {
      httpParams = httpParams.append('aiService', filters.aiService.join(','));
    }

    // Source filter (Rule Based Analysis)
    if (filters.source && filters.source.length > 0) {
      httpParams = httpParams.append('source', filters.source.join(','));
    }

    // Classification filter (incident/exposure)
    if (filters.classification) {
      httpParams = httpParams.append('classification', filters.classification);
    }

    // First seen date range
    if (filters.firstSeenFromDate) {
      httpParams = httpParams.append('firstSeenSince', filters.firstSeenFromDate.toISOString());
    }
    if (filters.firstSeenToDate) {
      httpParams = httpParams.append('firstSeenUntil', filters.firstSeenToDate.toISOString());
    }

    return httpParams;
  }
}
